import { useEffect, useState } from "react";
import { getSupabase } from "@/lib/supabase-lazy";
import { useDeferredMount } from "@/hooks/useDeferredMount";

export type VipTierKey = "arranque" | "crucero" | "partner";

export interface IntervalPrice {
  cents: number;
  euros: number; // integer euros
  label: string; // e.g. "299€"
  period: string; // localized, e.g. "/mes"
}

export interface VipTierPricing {
  monthly: IntervalPrice;
  annual: IntervalPrice & { savingsLabel: string };
}

export interface VipPricing {
  panel: { cents: number; euros: number; label: string; period: string }; // annual
  tiers: Record<VipTierKey, VipTierPricing>;
  loading: boolean;
}

const formatEur = (cents: number) => {
  const eur = Math.round(cents / 100);
  return `${eur.toLocaleString("es-ES")}€`;
};

const TIER_KEYS: VipTierKey[] = ["arranque", "crucero", "partner"];

export type VipPricingMode = "addon" | "combined";

const FALLBACKS: Record<VipPricingMode, Record<VipTierKey, { m: number; y: number }>> = {
  addon:    { arranque: { m: 28000, y: 280000 }, crucero: { m: 48000, y: 480000 }, partner: { m: 80000, y: 800000 } },
  combined: { arranque: { m: 28000, y: 280000 }, crucero: { m: 48000, y: 480000 }, partner: { m: 80000, y: 800000 } },
};

export const useVipPricing = (es: boolean = true, mode: VipPricingMode = "addon"): VipPricing => {
  const canSync = useDeferredMount(Infinity);
  const fb = FALLBACKS[mode];
  const [data, setData] = useState<Omit<VipPricing, "loading">>({
    panel: { cents: 99000, euros: 990, label: "990€", period: es ? "/año" : "/yr" },
    tiers: {
      arranque: {
        monthly: { cents: fb.arranque.m, euros: fb.arranque.m / 100, label: formatEur(fb.arranque.m), period: es ? "/mes" : "/mo" },
        annual:  { cents: fb.arranque.y, euros: fb.arranque.y / 100, label: formatEur(fb.arranque.y), period: es ? "/año" : "/yr", savingsLabel: es ? `Ahorras ${formatEur(fb.arranque.m * 12 - fb.arranque.y)}/año` : `Save ${formatEur(fb.arranque.m * 12 - fb.arranque.y)}/yr` },
      },
      crucero: {
        monthly: { cents: fb.crucero.m, euros: fb.crucero.m / 100, label: formatEur(fb.crucero.m), period: es ? "/mes" : "/mo" },
        annual:  { cents: fb.crucero.y, euros: fb.crucero.y / 100, label: formatEur(fb.crucero.y), period: es ? "/año" : "/yr", savingsLabel: es ? `Ahorras ${formatEur(fb.crucero.m * 12 - fb.crucero.y)}/año` : `Save ${formatEur(fb.crucero.m * 12 - fb.crucero.y)}/yr` },
      },
      partner: {
        monthly: { cents: fb.partner.m, euros: fb.partner.m / 100, label: formatEur(fb.partner.m), period: es ? "/mes" : "/mo" },
        annual:  { cents: fb.partner.y, euros: fb.partner.y / 100, label: formatEur(fb.partner.y), period: es ? "/año" : "/yr", savingsLabel: es ? `Ahorras ${formatEur(fb.partner.m * 12 - fb.partner.y)}/año` : `Save ${formatEur(fb.partner.m * 12 - fb.partner.y)}/yr` },
      },
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canSync) return;
    let cancelled = false;
    const tierType = mode === "combined" ? "vip_combined" : "mentor_addon";
    (async () => {
      const supabase = await getSupabase();
      const { data: allRows } = await (supabase as any).rpc("get_public_pricing");
      const rows = ((allRows ?? []) as any[]).filter(
        (r: any) => r.product_type === tierType || r.product_type === "pro"
      );
      if (!allRows || cancelled) {
        setLoading(false);
        return;
      }

      const panelYear = rows.find((r: any) => r.product_type === "pro" && r.product_key === "pro" && r.billing_interval === "year");
      const panelMonth = rows.find((r: any) => r.product_type === "pro" && r.product_key === "pro" && r.billing_interval === "month");
      const panelCents = panelYear?.price_cents ?? (panelMonth ? panelMonth.price_cents * 10 : 99000);

      const tiers: Record<VipTierKey, VipTierPricing> = { ...data.tiers };
      for (const key of TIER_KEYS) {
        const month = rows.find((r: any) => r.product_type === tierType && r.product_key === key && r.billing_interval === "month");
        const year = rows.find((r: any) => r.product_type === tierType && r.product_key === key && r.billing_interval === "year");
        const monthCents = month?.price_cents ?? 0;
        const yearCents = year?.price_cents ?? monthCents * 10;
        if (monthCents <= 0) continue;
        const savings = monthCents * 12 - yearCents;
        tiers[key] = {
          monthly: { cents: monthCents, euros: Math.round(monthCents / 100), label: formatEur(monthCents), period: es ? "/mes" : "/mo" },
          annual: {
            cents: yearCents,
            euros: Math.round(yearCents / 100),
            label: formatEur(yearCents),
            period: es ? "/año" : "/yr",
            savingsLabel: savings > 0
              ? es ? `Ahorras ${formatEur(savings)}/año` : `Save ${formatEur(savings)}/yr`
              : "",
          },
        };
      }

      setData({
        panel: { cents: panelCents, euros: Math.round(panelCents / 100), label: formatEur(panelCents), period: es ? "/año" : "/yr" },
        tiers,
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [es, mode, canSync]);

  return { ...data, loading };
};
